import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useToast,
  Badge,
  HStack,
  VStack,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { FiEdit, FiTrash2, FiEye, FiPlus, FiCheck } from 'react-icons/fi';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './TermosDeUsoAdmin.css';

// API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

const TermosDeUsoAdmin = () => {
  const [termosList, setTermosList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: 'Termos de Uso',
    content: '',
    version: '',
    isActive: false
  });
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [viewingTermos, setViewingTermos] = useState(null);
  
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  // Quill editor modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  useEffect(() => {
    fetchTermos();
  }, []);

  const fetchTermos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/termos-de-uso`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setTermosList(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar termos:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar termos de uso',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (termos = null) => {
    if (termos) {
      setFormData({
        title: termos.title,
        content: termos.content,
        version: termos.version,
        isActive: termos.isActive
      });
      setEditingId(termos._id);
    } else {
      // For new terms, suggest next version
      const latestVersion = termosList.length > 0 
        ? Math.max(...termosList.map(t => parseFloat(t.version) || 0))
        : 0;
      setFormData({
        title: 'Termos de Uso',
        content: '',
        version: (latestVersion + 0.1).toFixed(1),
        isActive: false
      });
      setEditingId(null);
    }
    onOpen();
  };

  const handleCloseModal = () => {
    setFormData({
      title: 'Termos de Uso',
      content: '',
      version: '',
      isActive: false
    });
    setEditingId(null);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      if (!formData.content || !formData.version) {
        toast({
          title: 'Erro',
          description: 'Por favor, preencha todos os campos obrigatórios',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const token = localStorage.getItem('token');
      const endpoint = editingId 
        ? `${API_BASE_URL}/api/termos-de-uso/${editingId}`
        : `${API_BASE_URL}/api/termos-de-uso`;
      
      const method = editingId ? 'put' : 'post';
      
      const response = await axios[method](endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        toast({
          title: 'Sucesso',
          description: editingId 
            ? 'Termos de uso atualizado com sucesso'
            : 'Termos de uso criado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchTermos();
        handleCloseModal();
      }
    } catch (error) {
      console.error('Erro ao salvar termos:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao salvar termos de uso',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleActivate = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_BASE_URL}/api/termos-de-uso/${id}/activate`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data.success) {
        toast({
          title: 'Sucesso',
          description: 'Termos de uso ativado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchTermos();
      }
    } catch (error) {
      console.error('Erro ao ativar termos:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao ativar termos de uso',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_BASE_URL}/api/termos-de-uso/${deletingId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data.success) {
        toast({
          title: 'Sucesso',
          description: 'Termos de uso excluído com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchTermos();
        onDeleteClose();
        setDeletingId(null);
      }
    } catch (error) {
      console.error('Erro ao excluir termos:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao excluir termos de uso',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleView = (termos) => {
    setViewingTermos(termos);
    onViewOpen();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxW="container.xl" py={5}>
      <VStack spacing={5} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg">Gerenciar Termos de Uso</Heading>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="green"
            onClick={() => handleOpenModal()}
          >
            Novo Termo
          </Button>
        </HStack>

        {loading ? (
          <Box textAlign="center" py={10}>
            <Spinner size="xl" color="green.500" />
          </Box>
        ) : termosList.length === 0 ? (
          <Alert status="info">
            <AlertIcon />
            Nenhum termo de uso cadastrado. Clique em "Novo Termo" para criar o primeiro.
          </Alert>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Título</Th>
                  <Th>Versão</Th>
                  <Th>Status</Th>
                  <Th>Criado em</Th>
                  <Th>Atualizado em</Th>
                  <Th>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {termosList.map((termo) => (
                  <Tr key={termo._id}>
                    <Td>{termo.title}</Td>
                    <Td>{termo.version}</Td>
                    <Td>
                      <Badge colorScheme={termo.isActive ? 'green' : 'gray'}>
                        {termo.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </Td>
                    <Td>{formatDate(termo.createdAt)}</Td>
                    <Td>{formatDate(termo.updatedAt)}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Visualizar"
                          icon={<FiEye />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleView(termo)}
                        />
                        <IconButton
                          aria-label="Editar"
                          icon={<FiEdit />}
                          size="sm"
                          colorScheme="yellow"
                          variant="ghost"
                          onClick={() => handleOpenModal(termo)}
                        />
                        {!termo.isActive && (
                          <>
                            <IconButton
                              aria-label="Ativar"
                              icon={<FiCheck />}
                              size="sm"
                              colorScheme="green"
                              variant="ghost"
                              onClick={() => handleActivate(termo._id)}
                            />
                            <IconButton
                              aria-label="Excluir"
                              icon={<FiTrash2 />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => {
                                setDeletingId(termo._id);
                                onDeleteOpen();
                              }}
                            />
                          </>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>
            {editingId ? 'Editar Termos de Uso' : 'Criar Novos Termos de Uso'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Título</FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Termos de Uso"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Versão</FormLabel>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="Ex: 1.0"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Conteúdo</FormLabel>
                <Box className="quill-container">
                  <ReactQuill
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    modules={modules}
                    theme="snow"
                    placeholder="Digite o conteúdo dos termos de uso..."
                    style={{ height: '300px', marginBottom: '50px' }}
                  />
                </Box>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">
                  Ativar este termo?
                </FormLabel>
                <Switch
                  isChecked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  colorScheme="green"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button colorScheme="green" onClick={handleSubmit}>
              {editingId ? 'Atualizar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>
            {viewingTermos?.title} - Versão {viewingTermos?.version}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <HStack>
                <Text fontWeight="bold">Status:</Text>
                <Badge colorScheme={viewingTermos?.isActive ? 'green' : 'gray'}>
                  {viewingTermos?.isActive ? 'Ativo' : 'Inativo'}
                </Badge>
              </HStack>
              <Box>
                <Text fontWeight="bold" mb={2}>Conteúdo:</Text>
                <Box
                  p={4}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  dangerouslySetInnerHTML={{ __html: viewingTermos?.content }}
                  className="terms-preview"
                />
              </Box>
              <HStack spacing={4} fontSize="sm" color="gray.600">
                <Text>Criado em: {viewingTermos && formatDate(viewingTermos.createdAt)}</Text>
                <Text>Atualizado em: {viewingTermos && formatDate(viewingTermos.updatedAt)}</Text>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir Termos de Uso
            </AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja excluir este termo de uso? Esta ação não pode ser desfeita.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default TermosDeUsoAdmin;